import { fetchSuggestions } from "./bloomreach-suggest";

const CARD_SECTION_ID = "header-search-card";
const MAX_PRODUCTS = 3;
const MAX_QUERIES = 5;
const DEBOUNCE_MS = 300;
const QUERY_TOKEN = "[[query]]";

const cardRequests = new Map();

/**
 * A product's card markup, rendered by the header-search-card section.
 * Resolves null for a product that can't be loaded or isn't for sale.
 *
 * Markup rather than elements is cached: a card element re-inserted after
 * being removed would run its connectedCallback again and double its listeners.
 */
function loadCardMarkup(path) {
  if (cardRequests.has(path)) return cardRequests.get(path);

  const url = new URL(path, window.location.origin);
  url.searchParams.set("section_id", CARD_SECTION_ID);

  const request = fetch(url)
    .then((response) => (response.ok ? response.text() : null))
    .then((markup) => (markup?.includes("<product-card") ? markup : null))
    .catch(() => null);

  cardRequests.set(path, request);

  return request;
}

const parseCards = (markup) => [
  ...new DOMParser()
    .parseFromString(markup, "text/html")
    .querySelectorAll("product-card"),
];

/**
 * Cards for the first `MAX_PRODUCTS` handles that are for sale, in order.
 * Fetched a row at a time, so a sold-out product is only made up for when
 * there is one.
 */
async function loadProductCards(handles) {
  const cards = [];

  for (
    let start = 0;
    start < handles.length && cards.length < MAX_PRODUCTS;
    start += MAX_PRODUCTS
  ) {
    const batch = await Promise.all(
      handles
        .slice(start, start + MAX_PRODUCTS)
        .map((handle) => loadCardMarkup(window.productUrl(handle))),
    );
    cards.push(...batch.filter(Boolean));
  }

  return cards.slice(0, MAX_PRODUCTS).flatMap(parseCards);
}

/** `text` with every case-insensitive match of `term` wrapped in a span. */
function highlight(text, term) {
  const fragment = document.createDocumentFragment();
  const haystack = text.toLowerCase();
  const needle = term.toLowerCase();
  let index = 0;

  for (
    let match = haystack.indexOf(needle);
    needle && match !== -1;
    match = haystack.indexOf(needle, index)
  ) {
    const span = document.createElement("span");
    span.className = "header-search__suggestion-match";
    span.textContent = text.slice(match, match + needle.length);

    fragment.append(text.slice(index, match), span);
    index = match + needle.length;
  }

  fragment.append(text.slice(index));

  return fragment;
}

/**
 * The header search panel. Suggestions come from Bloomreach; products are
 * shown as our own cards, quick add included.
 */
class HeaderSearch extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector("[data-search-button]");
    this.panel = this.querySelector("[data-search-panel]");
    this.form = this.querySelector("form");
    this.input = this.querySelector("[data-search-input]");
    this.clearButton = this.querySelector("[data-search-clear]");
    this.closeButton = this.querySelector("[data-search-close]");
    this.defaultContent = this.querySelector("[data-search-default]");
    this.popular = this.querySelector("[data-search-popular]");
    this.noResults = this.querySelector("[data-search-no-results]");
    this.noResultsMessage = this.querySelector(
      "[data-search-no-results-message]",
    );
    this.results = this.querySelector("[data-search-results]");
    this.suggestions = this.querySelector("[data-search-suggestions]");
    this.products = this.querySelector("[data-search-products]");
    this.viewAll = this.querySelector("[data-search-view-all]");

    this.button.addEventListener("click", this._toggle);
    this.closeButton.addEventListener("click", this._onClose);
    this.clearButton.addEventListener("click", this._onClear);
    this.input.addEventListener("input", this._onInput);
    document.addEventListener("click", this._onDocumentClick);
    document.addEventListener("keydown", this._onKeydown);
    document.addEventListener("menu:open", this._onMenuOpen);
  }

  disconnectedCallback() {
    clearTimeout(this.debounceTimeout);
    this.abortController?.abort();
    document.removeEventListener("click", this._onDocumentClick);
    document.removeEventListener("keydown", this._onKeydown);
    document.removeEventListener("menu:open", this._onMenuOpen);

    if (this.isOpen) this._close();
  }

  _toggle = () => {
    this.isOpen ? this._close(this.button) : this._open();
  };

  _open = () => {
    if (this.isOpen) return;

    this.isOpen = true;
    this.button.setAttribute("aria-expanded", "true");
    this.panel.hidden = false;
    this.style.setProperty(
      "--header-search-top",
      `${this.closest(".header").getBoundingClientRect().bottom}px`,
    );

    window.lockScroll(this);
    window.openDimmer(this);
    window.trapFocus(this.panel, this.input);
  };

  /** @param {HTMLElement} [focusTarget] - Where focus goes once the panel is closed. */
  _close = (focusTarget = null) => {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.button.setAttribute("aria-expanded", "false");
    this.panel.hidden = true;

    window.unlockScroll(this);
    window.closeDimmer(this);
    window.removeTrapFocus(focusTarget);
  };

  _onClose = () => {
    this._clear();
    this._close(this.button);
  };

  _onClear = () => {
    this._clear();
    this.input.focus();
  };

  _onDocumentClick = (e) => {
    // The quick add modal opens over the panel from one of its cards.
    if (
      !this.isOpen ||
      this.contains(e.target) ||
      e.target.closest?.("markup-modal")
    ) {
      return;
    }

    this._close();
  };

  _onKeydown = (e) => {
    if (e.key !== "Escape" || e.target.closest?.("markup-modal")) return;

    this._close(this.button);
  };

  _onMenuOpen = (e) => {
    if (!this.contains(e.target)) this._close();
  };

  _onInput = () => {
    const query = this.input.value.trim();

    this.clearButton.hidden = !this.input.value;
    clearTimeout(this.debounceTimeout);

    if (!query) {
      this.abortController?.abort();
      this._showDefault();
      return;
    }

    this.debounceTimeout = setTimeout(() => this._search(query), DEBOUNCE_MS);
  };

  _clear() {
    this.input.value = "";
    this._onInput();
  }

  async _search(query) {
    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;

    let suggestions;

    try {
      suggestions = await fetchSuggestions(query, {
        env: this.dataset.bloomreachEnv,
        signal: controller.signal,
      });
    } catch (error) {
      if (error.name === "AbortError") return;
      suggestions = { queries: [], products: [] };
    }

    if (!suggestions.queries.length || !suggestions.products.length) {
      this._showNoResults(query);
      return;
    }

    const cards = await loadProductCards(
      suggestions.products.map(({ pid }) => pid).filter(Boolean),
    );

    if (controller.signal.aborted) return;

    this._showResults(query, suggestions.queries.slice(0, MAX_QUERIES), cards);
  }

  _searchUrl(query) {
    const url = new URL(this.form.action);
    url.searchParams.set("q", query);

    return url.toString();
  }

  _showDefault() {
    this.results.hidden = true;
    this.defaultContent.hidden = false;
    this.popular.hidden = !this.popular.querySelector("a");
    this.noResults.hidden = true;
  }

  _showNoResults(query) {
    const [before, after = ""] = (this.dataset.noResultsMessage ?? "").split(
      QUERY_TOKEN,
    );
    const term = document.createElement("strong");
    term.textContent = `“${query}”`;

    this.noResultsMessage.replaceChildren(before, term, after);

    this.results.hidden = true;
    this.defaultContent.hidden = false;
    this.popular.hidden = true;
    this.noResults.hidden = false;
  }

  _showResults(query, queries, cards) {
    this.suggestions.replaceChildren(
      ...queries.map((suggestion) => {
        const item = document.createElement("li");
        const link = document.createElement("a");

        link.className = "header-search__suggestion";
        link.href = this._searchUrl(suggestion);
        link.append(highlight(suggestion, query));
        item.append(link);

        return item;
      }),
    );

    this.products.replaceChildren(...cards);
    this.viewAll.href = this._searchUrl(query);

    this.defaultContent.hidden = true;
    this.results.hidden = false;
  }
}

if (!customElements.get("header-search")) {
  customElements.define("header-search", HeaderSearch);
}
